import { useState } from "react";
import { ChevronDown, Coins, Hourglass, MapPin, Package, Send, Sparkles, Zap } from "lucide-react";
import { DOLOPALS, MISSIONS, TRAIT_COLORS } from "../../data/dolopals";
import { SectionHeading } from "../SectionHeading";
import { fmt, signalOfTheDay, titanLevel } from "./utils";

export function TitanExpeditions({ exp, now, send, claim, collection }) {
  const [pick, setPick] = useState({}); // missionId -> titanId
  const [burst, setBurst] = useState(null); // { missionId, key, parts }
  const owned = DOLOPALS.filter((p) => collection.includes(p.id));
  const ownedElsewhere = (titanId, missionId) => Boolean(exp.active[titanId]);

  // flying scrap-shard burst when loot is claimed
  const fireBurst = (missionId) => {
    const parts = Array.from({ length: 16 }, (_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 55 + Math.random() * 95;
      return {
        id: i,
        tx: Math.round(Math.cos(angle) * dist),
        ty: Math.round(Math.sin(angle) * dist - 55),
        rot: Math.floor(Math.random() * 360),
        dl: (Math.random() * 0.12).toFixed(2),
      };
    });
    setBurst({ missionId, key: Date.now(), parts });
    window.setTimeout(() => setBurst((b) => (b && b.missionId === missionId ? null : b)), 1050);
  };

  return (
    <section className="titan-expeditions" data-testid="titan-expeditions">
      <SectionHeading
        eyebrow="IDLE OPS // WORKER PLACEMENT"
        title="Titan expeditions."
        text="Send your adopted titans into the wastes. Matching a titan's trait to the mission pays 1.5x shards, bonus XP, and lore fragments. They keep working while you're gone."
      />
      <div className="exp-wallet" data-testid="exp-wallet">
        <span><Coins size={13} /> <b data-testid="exp-shards">{exp.shards}</b> SCRAP-SHARDS</span>
        <span><Package size={13} /> <b data-testid="exp-fragments">{exp.fragments}</b> LORE FRAGMENTS</span>
        <span><Hourglass size={13} /> TITANS AFIELD: <b data-testid="exp-active-count">{Object.keys(exp.active).length}</b></span>
        <span className="exp-wallet-sotd"><Zap size={13} /> SIGNAL OF THE DAY: <b data-testid="exp-sotd">{MISSIONS.find((m) => m.id === signalOfTheDay())?.name} PAYS 2X</b></span>
      </div>

      {owned.length === 0 ? (
        <p className="exp-empty" data-testid="exp-empty">
          NO TITANS ADOPTED YET — UNBOX A DROP ABOVE, THEN COME BACK AND PUT THEM TO WORK.
        </p>
      ) : (
        <div className="exp-grid">
          {MISSIONS.map((m) => {
            const chosen = pick[m.id] || owned[0].id;
            const chosenPal = DOLOPALS.find((p) => p.id === chosen);
            const runner = owned.find((p) => exp.active[p.id] && exp.active[p.id].missionId === m.id);
            const run = runner ? exp.active[runner.id] : null;
            const done = run && run.endsAt <= now;
            const match = chosenPal && chosenPal.trait === m.trait;
            return (
              <article key={m.id} className={`exp-card ${run ? "is-running" : ""} ${done ? "is-done" : ""}`} data-testid={`exp-card-${m.id}`}>
                <div className="exp-card-head">
                  <MapPin size={14} />
                  <h3>{m.name}</h3>
                  <span className="exp-trait" style={{ "--tc": TRAIT_COLORS[m.trait] }}>{m.trait} FIT</span>
                  {m.id === signalOfTheDay() ? <span className="exp-sotd" data-testid={`exp-sotd-${m.id}`}>2X TODAY</span> : null}
                </div>
                <p>{m.desc}</p>
                <div className="exp-meta">
                  <span>{fmt(m.seconds * 1000)} RUN</span>
                  <span>{m.shards[0]}–{m.shards[1]} SHARDS</span>
                  <span>+{m.xp} XP</span>
                </div>
                {run && runner ? (
                  <div className="exp-running" data-testid={`exp-running-${m.id}`}>
                    <div className="exp-runner-line">
                      <img src={runner.art} alt={runner.name} />
                      <strong>{runner.name} LVL {titanLevel(exp.xp[runner.id])}</strong>
                      {done ? <em>BACK FROM THE WASTES!</em> : <em>{fmt(run.endsAt - now)} LEFT</em>}
                    </div>
                    <div className="exp-progress">
                      <span style={{ width: `${Math.min(100, 100 - ((run.endsAt - now) / (m.seconds * 1000)) * 100)}%` }} />
                    </div>
                    {done ? (
                      <button type="button" className="exp-claim" onClick={() => { fireBurst(m.id); claim(runner.id); }} data-testid={`exp-claim-${m.id}`}>
                        <Sparkles size={13} /> CLAIM LOOT
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <div className="exp-send">
                    <div className="exp-pick">
                      <select
                        value={chosen}
                        onChange={(e) => setPick((p) => ({ ...p, [m.id]: e.target.value }))}
                        data-testid={`exp-pick-${m.id}`}
                        aria-label={`Choose titan for ${m.name}`}
                      >
                        {owned.map((p) => (
                          <option key={p.id} value={p.id} disabled={ownedElsewhere(p.id, m.id)}>
                            {p.name} · LVL {titanLevel(exp.xp[p.id])} · {p.trait}{p.trait === m.trait ? " ★ BEST FIT" : ""}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={14} />
                    </div>
                    <button
                      type="button"
                      className={`exp-send-btn ${match ? "is-match" : ""}`}
                      onClick={() => send(chosen, m.id)}
                      disabled={!chosenPal || ownedElsewhere(chosen, m.id)}
                      data-testid={`exp-send-${m.id}`}
                    >
                      <Send size={13} /> SEND{match ? " — BEST FIT ×1.5" : ""}
                    </button>
                  </div>
                )}
                {burst && burst.missionId === m.id ? (
                  <div className="loot-burst" key={burst.key} data-testid="loot-burst">
                    {burst.parts.map((p) => (
                      <span
                        key={p.id}
                        className="loot-shard"
                        style={{ "--tx": `${p.tx}px`, "--ty": `${p.ty}px`, "--rot": `${p.rot}deg`, "--dl": `${p.dl}s` }}
                      />
                    ))}
                    <em className="loot-text">LOOT SECURED</em>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
